import React, { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';

import { isMobile, resizeContent, setTranslationValues } from '../../../helpers';
import { AspectRatioImage } from '../features/cashback/ratio-image';
import { useStore, useTranslation } from '../../../hooks';
import { formatTime } from '../../../jackpot/helpers';
import { T } from '../../../constants';

import WarningIcon from '../../../assets/media/maintenance/warning.svg';

import './style.scss';

// Rounded up, so 00:00 shows only once the deadline is really reached.
const getSecondsLeft = (deadline: number): number => Math.ceil(Math.max(0, deadline - Date.now()) / 1000);

/**
 * @description top-center warning for a `MaintenanceNotify` push: counts down to the deadline, then says
 * the session ends with the running round. `maintenanceStore` owns the timing; this only renders it.
 * Laid out inside the game frame like the jackpot header: the 16:9 box on web (scaled from the 1920x1080
 * design), the `canvasWidth` column for portrait games on web, the whole screen on mobile.
 */
export const MaintenanceNotify: React.FC = observer(() => {

  const { maintenanceStore, gameStore } = useStore();
  const { showBanner, isAwaitingRound, deadline } = maintenanceStore;
  const { canvasWidth, gameReady } = gameStore;
  const t = useTranslation();

  const bannerRef = useRef<HTMLDivElement>(null);
  const [secondsLeft, setSecondsLeft] = useState(() => getSecondsLeft(deadline));

  // Over the game only, like the other platform features; the countdown itself runs on in the store.
  const visible = showBanner && gameReady;
  const isWebWithCanvas = !isMobile() && !!canvasWidth;
  const scaleWithFrame = !isMobile() && !canvasWidth;

  useEffect(() => {
    if (!visible || isAwaitingRound) return;
    setSecondsLeft(getSecondsLeft(deadline));
    // Ticks faster than a second so the display never lags the deadline by a whole step.
    const interval = setInterval(() => setSecondsLeft(getSecondsLeft(deadline)), 250);
    return () => clearInterval(interval);
  }, [visible, isAwaitingRound, deadline]);

  useEffect(() => {
    const element = bannerRef.current;
    if (!element) return;
    if (!scaleWithFrame) {
      element.style.removeProperty('zoom');
      return;
    }
    const resize = (): void => { resizeContent({ target: element }); };
    resize();
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, [visible, scaleWithFrame]);

  if (!visible) return null;

  const text = isAwaitingRound
    ? t(T.MAINTENANCE_NOTIFY_AFTER_ROUND_TEXT)
    : setTranslationValues(t(T.MAINTENANCE_NOTIFY_TEXT), { time: formatTime(secondsLeft * 1000) });

  const content = (
    <div
      className="maintenance-notify-container"
      style={isWebWithCanvas ? { maxWidth: canvasWidth } : {}}
    >
      {scaleWithFrame && <AspectRatioImage />}
      <div ref={bannerRef} className="maintenance-notify" role="alert">
        <img className="maintenance-notify-icon" src={WarningIcon} alt="" />
        <p className="maintenance-notify-text">{text}</p>
      </div>
    </div>
  );

  if (isWebWithCanvas) {
    return <div className="is-mobile maintenance-notify-canvas-mobile">{content}</div>;
  }
  return content;
});
